import { Category, DefaultProjectKey } from './enums';

/** Project palette (data dictionary §6): palette name to colour value. */
export const PALETTE = {
  blue: '#2F6FDE',
  teal: '#0B8191',
  green: '#16874F',
  olive: '#6F8210',
  amber: '#B58900',
  orange: '#DB4F08',
  red: '#D6393B',
  pink: '#D63384',
  purple: '#8250DF',
  brown: '#8A5A3B',
} as const;

export type PaletteColor = keyof typeof PALETTE;

export const PALETTE_COLORS = Object.keys(PALETTE) as [PaletteColor, ...PaletteColor[]];

/**
 * Display aliases of retired colour names (data dictionary §6): `gray` was only ever the
 * default sleep colour, so it maps to the new default sleep colour; `stone` maps to `orange`.
 * Resolved on display only — the stored value is kept until the user picks a new colour.
 */
export const PALETTE_ALIASES: Readonly<Record<string, PaletteColor>> = {
  gray: 'teal',
  stone: 'orange',
};

/**
 * Colour name every unknown key degrades to on display (data dictionary §6): `teal`, which also
 * stands in for a missing project. (`brown` is a selectable colour, not the fallback.)
 */
export const FALLBACK_PALETTE_COLOR: PaletteColor = 'teal';

function isPaletteColor(color: string): color is PaletteColor {
  return Object.prototype.hasOwnProperty.call(PALETTE, color);
}

/**
 * Palette name a stored colour name is displayed as. A stored name is a bounded string, not an
 * enum (data dictionary §3): retired names follow the alias table, and a name this build does
 * not know — for example one written by a newer client — falls back without rejecting the
 * record. The stored value is never rewritten.
 */
export function resolvePaletteColor(color: string): PaletteColor {
  if (isPaletteColor(color)) return color;
  return Object.prototype.hasOwnProperty.call(PALETTE_ALIASES, color)
    ? (PALETTE_ALIASES[color] as PaletteColor)
    : FALLBACK_PALETTE_COLOR;
}

/** Colour value (hex) a stored colour name is displayed as (aliases and fallback included). */
export function paletteHex(color: string): string {
  return PALETTE[resolvePaletteColor(color)];
}

export interface DefaultProjectSpec {
  readonly key: DefaultProjectKey;
  readonly color: PaletteColor;
  readonly category: Category;
  readonly sort: number;
}

/** Built-in default projects: work, sleep / nap, household chores (requirements §3.5). */
export const DEFAULT_PROJECTS: readonly DefaultProjectSpec[] = [
  { key: DefaultProjectKey.Work, color: 'blue', category: Category.Work, sort: 0 },
  { key: DefaultProjectKey.Sleep, color: 'teal', category: Category.Rest, sort: 1 },
  { key: DefaultProjectKey.Chores, color: 'orange', category: Category.Life, sort: 2 },
];

/**
 * Colour given to a project created without a colour choice (2026-10b §2.5, §5.7): the first
 * palette colour, in palette order, that no project uses yet — deactivated projects count,
 * and a retired alias counts as the colour it displays as. When every colour is taken, the
 * palette wraps around by the number of projects. Shared by the settings page and the inline
 * "new project" forms.
 */
export function nextProjectColor(projects: readonly { readonly data: { readonly color: string } }[]): PaletteColor {
  const used = new Set(projects.map((project) => resolvePaletteColor(project.data.color)));
  const free = PALETTE_COLORS.find((color) => !used.has(color));
  return free ?? PALETTE_COLORS[projects.length % PALETTE_COLORS.length] ?? PALETTE_COLORS[0];
}
